import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Building, Calendar, DollarSign, FileText, Save, Scale, Tag, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { FormActions, FormField, FormPageHeader, FormSection } from '@/components/common/FormSection'
import { TagsInput } from '@/components/common/TagsInput'
import {
  CreateCaseDto,
  CaseArea,
  CasePriority,
  CaseAreaLabels,
  CasePriorityLabels
} from '../types/case'
import { Client } from '../types/client'
import { caseService } from '../services/case-service'
import { clientService } from '../services/client-service'
import { useDateTimeFormat } from '../hooks/useDateTimeFormat'

import { getErrorMessage } from '../utils/error.utils'
import { NativeSelect } from '@/components/ui/native-select'

export function CreateCasePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { prepareDateForSubmit } = useDateTimeFormat()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [, setLoadingClients] = useState(true)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [clientNameInput, setClientNameInput] = useState('')
  const [showClientDropdown, setShowClientDropdown] = useState(false)

  // Obter parâmetros da URL para conversão de atendimento
  const clientIdParam = searchParams.get('clientId')
  const isLegalParam = searchParams.get('isLegal') === 'true'

  const [formData, setFormData] = useState<CreateCaseDto>({
    title: '',
    description: '',
    clientId: clientIdParam || '',
    area: CaseArea.CIVIL,
    priority: CasePriority.MEDIUM,
    courtName: isLegalParam ? '' : undefined,
    courtNumber: isLegalParam ? '' : undefined,
    judgeName: isLegalParam ? '' : undefined,
    estimatedValue: undefined,
    successFeePercentage: undefined,
    responsibleLawyerId: '',
    responsibleLawyerName: '',
    teamMembers: [],
    startDate: new Date().toISOString().split('T')[0],
    expectedEndDate: '',
    tags: isLegalParam ? ['processo judicial'] : []
  })

  useEffect(() => {
    loadClients()
  }, [])

  const loadClientById = useCallback(async (clientId: string) => {
    try {
      const client = await clientService.getClientById(clientId)
      setSelectedClient(client)
      setClientNameInput(getClientDisplayName(client))
      setFormData(prev => ({ ...prev, clientId }))
    } catch (err) {
      console.error('Error loading client:', err)
    }
  }, [])

  useEffect(() => {
    if (clientIdParam) {
      loadClientById(clientIdParam)
    }
  }, [clientIdParam, loadClientById])

  const loadClients = async () => {
    try {
      setLoadingClients(true)
      const response = await clientService.getAllClients({ status: 'ACTIVE' }, { page: 1, limit: 100, sort: 'name:asc' })
      setClients(response.clients)
    } catch (err) {
      console.error('Erro ao carregar clientes:', err)
    } finally {
      setLoadingClients(false)
    }
  }

  const getClientDisplayName = (client: Client): string => {
    if (client.clientType === 'PERSON' && client.personalData) {
      return client.personalData.fullName || 'Sem nome'
    } else if (client.clientType === 'COMPANY' && client.companyData) {
      return client.companyData.tradingName || client.companyData.companyName || 'Sem nome'
    }
    return 'Sem nome'
  }

  const filteredClients = clients.filter(client => {
    const displayName = getClientDisplayName(client).toLowerCase()
    return displayName.includes(clientNameInput.toLowerCase())
  })

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client)
    setClientNameInput(getClientDisplayName(client))
    setFormData(prev => ({ ...prev, clientId: client.id }))
    setShowClientDropdown(false)
  }

  const handleInputChange = (field: keyof CreateCaseDto, value: unknown) => {
    setFormData({
      ...formData,
      [field]: value
    })
  }

  const addTag = (tag: string) => {
    if (tag.trim() && !formData.tags?.includes(tag.trim())) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tag.trim()]
      })
    }
  }

  const removeTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter(tag => tag !== tagToRemove) || []
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim()) {
      setError('O título é obrigatório')
      return
    }

    if (!clientNameInput.trim()) {
      setError('Digite o nome do cliente')
      return
    }

    try {
      setLoading(true)
      setError(null)

      let clientId = formData.clientId

      // Se não houver um cliente selecionado, criar um novo (cadastro simplificado)
      if (!clientId) {
        const trimmedName = clientNameInput.trim()
        const nameParts = trimmedName.split(/\s+/)
        const firstName = nameParts[0] || trimmedName
        const lastName = nameParts.slice(1).join(' ') || firstName
        const newClient = await clientService.createClient({
          clientType: 'PERSON',
          personalData: {
            fullName: trimmedName,
            firstName,
            lastName,
            dateOfBirth: undefined,
            gender: 'NOT_SPECIFIED',
            maritalStatus: 'NOT_SPECIFIED',
            nationality: 'Brasileiro',
            profession: undefined,
            cpf: undefined,
            rg: undefined,
            rgIssuer: undefined
          },
          status: 'ACTIVE'
        })
        clientId = newClient.id
      }

      const caseData: CreateCaseDto = {
        ...formData,
        clientId,
        startDate: prepareDateForSubmit(formData.startDate) || formData.startDate,
        expectedEndDate: prepareDateForSubmit(formData.expectedEndDate) || undefined,
        estimatedValue: formData.estimatedValue || undefined,
        successFeePercentage: formData.successFeePercentage || undefined
      }

      await caseService.createCase(caseData)
      navigate('/cases')
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar caso'))
    } finally {
      setLoading(false)
    }
  }


  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <FormPageHeader
        title="Novo Caso"
        description="Preencha as informações básicas do caso. Os campos obrigatórios estão marcados com *."
        icon={Scale}
        backTo="/cases"
        backLabel="Voltar para casos"
      />

      <form onSubmit={handleSubmit} className="flex max-w-4xl flex-col gap-4">
        {error && (
          <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
        )}

        <FormSection title="Informações Básicas" icon={FileText}>
          <FormField label="Título do Caso *" htmlFor="title">
            <Input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Digite o título do caso"
              required
            />
          </FormField>

          <FormField label="Descrição" htmlFor="description">
            <Textarea
              id="description"
              rows={4}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Descreva os detalhes do caso"
            />
          </FormField>

          <FormField label="Cliente *" htmlFor="case-client-name">
            {selectedClient ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/40 p-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{getClientDisplayName(selectedClient)}</p>
                  <p className="text-sm text-muted-foreground">#{selectedClient.clientNumber}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remover cliente selecionado"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => {
                    setSelectedClient(null)
                    setClientNameInput('')
                    setFormData(prev => ({ ...prev, clientId: '' }))
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <Input
                    id="case-client-name"
                    type="text"
                    placeholder="Digite o nome do cliente..."
                    value={clientNameInput}
                    onChange={(e) => {
                      setClientNameInput(e.target.value)
                      setShowClientDropdown(true)
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                    autoComplete="off"
                  />
                  {showClientDropdown && clientNameInput && filteredClients.length > 0 && (
                    <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
                      {filteredClients.map(client => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleClientSelect(client)}
                          className="w-full px-3 py-2 text-left transition-colors hover:bg-muted"
                        >
                          <p className="font-medium">{getClientDisplayName(client)}</p>
                          <p className="text-sm text-muted-foreground">#{client.clientNumber}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {clientNameInput && filteredClients.length === 0 && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Nenhum cliente encontrado. Um novo cliente será criado com o nome &quot;{clientNameInput}&quot;.
                  </p>
                )}
              </div>
            )}
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField label="Área do Direito *" htmlFor="area">
              <NativeSelect id="area" value={formData.area} onChange={(e) => handleInputChange('area', e.target.value as CaseArea)}>
                {Object.entries(CaseAreaLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Prioridade" htmlFor="priority">
              <NativeSelect id="priority" value={formData.priority} onChange={(e) => handleInputChange('priority', e.target.value as CasePriority)}>
                {Object.entries(CasePriorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Advogado Responsável" htmlFor="responsibleLawyerName">
              <Input
                type="text"
                id="responsibleLawyerName"
                value={formData.responsibleLawyerName || ''}
                onChange={(e) => handleInputChange('responsibleLawyerName', e.target.value)}
                placeholder="Nome do advogado responsável"
              />
            </FormField>
          </div>
        </FormSection>

        <FormSection title="Informações do Tribunal" icon={Building} className="sm:grid-cols-3">
          <FormField label="Nome do Tribunal" htmlFor="courtName">
            <Input
              type="text"
              id="courtName"
              value={formData.courtName || ''}
              onChange={(e) => handleInputChange('courtName', e.target.value)}
              placeholder="Ex: Tribunal de Justiça de SP"
            />
          </FormField>
          <FormField label="Número do Processo" htmlFor="courtNumber">
            <Input
              type="text"
              id="courtNumber"
              value={formData.courtNumber || ''}
              onChange={(e) => handleInputChange('courtNumber', e.target.value)}
              placeholder="0000000-00.0000.0.00.0000"
            />
          </FormField>
          <FormField label="Nome do Juiz" htmlFor="judgeName">
            <Input
              type="text"
              id="judgeName"
              value={formData.judgeName || ''}
              onChange={(e) => handleInputChange('judgeName', e.target.value)}
              placeholder="Nome do juiz responsável"
            />
          </FormField>
        </FormSection>

        <FormSection title="Informações Financeiras" icon={DollarSign} className="sm:grid-cols-2">
          <FormField label="Valor Estimado (R$)" htmlFor="estimatedValue">
            <Input
              type="number"
              step="0.01"
              min="0"
              id="estimatedValue"
              value={formData.estimatedValue || ''}
              onChange={(e) => handleInputChange('estimatedValue', e.target.value ? parseFloat(e.target.value) : undefined)}
              placeholder="0.00"
            />
          </FormField>
          <FormField label="Porcentagem de Sucesso (%)" htmlFor="successFeePercentage">
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              id="successFeePercentage"
              value={formData.successFeePercentage || ''}
              onChange={(e) => handleInputChange('successFeePercentage', e.target.value ? parseFloat(e.target.value) : undefined)}
              placeholder="0.00"
            />
          </FormField>
        </FormSection>

        <FormSection title="Cronograma" icon={Calendar} className="sm:grid-cols-2">
          <FormField label="Data de Início *" htmlFor="startDate">
            <Input
              type="date"
              id="startDate"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              required
            />
          </FormField>
          <FormField label="Data de Término Esperada" htmlFor="expectedEndDate">
            <Input
              type="date"
              id="expectedEndDate"
              value={formData.expectedEndDate || ''}
              onChange={(e) => handleInputChange('expectedEndDate', e.target.value)}
              min={formData.startDate}
            />
          </FormField>
        </FormSection>

        <FormSection title="Tags" icon={Tag}>
          <TagsInput tags={formData.tags || []} onAdd={addTag} onRemove={removeTag} />
        </FormSection>

        <FormActions>
          <Button variant="outline" asChild><Link to="/cases">Cancelar</Link></Button>
          <Button type="submit" disabled={loading} loading={loading}>
            {loading ? 'Criando...' : <><Save className="size-4" /> Criar Caso</>}
          </Button>
        </FormActions>
      </form>
    </div>
  )
}
